import { useEffect } from 'react';
import { LOCALES, DEFAULT_LOCALE, localizedPath } from '@/i18n/config';
import { getLocalized, isLocaleMap } from '@/utils/localize';
import { upsertMeta, upsertLink, removeLink, replaceMetaGroup } from '@/utils/headTags';

const SITE_NAME = 'Morettino Caffè Palermo';

/** Aggiorna html lang, title, meta description/OG/Twitter, canonical e hreflang a ogni cambio pagina o lingua. */
export function PageMeta({ page, locale = DEFAULT_LOCALE }) {
  useEffect(() => {
    if (!page) return;

    document.documentElement.lang = locale;

    const metaTitle = getLocalized(page.metaTitle, locale) || getLocalized(page.metaTitle, DEFAULT_LOCALE);
    const metaDescription = getLocalized(page.metaDescription, locale) || getLocalized(page.metaDescription, DEFAULT_LOCALE);
    const title = getLocalized(page.title, locale) || getLocalized(page.title, DEFAULT_LOCALE);
    const fullTitle = metaTitle || `${title} · ${SITE_NAME}`;
    // Nessun campo dedicato lasciato vuoto: se l'admin non compila l'alt
    // dell'immagine di condivisione, riusa il title/description già scritti
    // invece di mandare un tag og:image senza descrizione.
    const ogImageAlt = getLocalized(page.ogImageAlt, locale) || getLocalized(page.ogImageAlt, DEFAULT_LOCALE) || metaTitle || title;

    const origin = window.location.origin;
    const canonicalUrl = origin + localizedPath(page.slug, locale);
    const currentLocaleConfig = LOCALES.find((l) => l.code === locale);

    document.title = fullTitle;
    upsertMeta('name', 'description', metaDescription);
    upsertLink('canonical', null, canonicalUrl);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:title', metaTitle || title);
    upsertMeta('property', 'og:description', metaDescription);
    upsertMeta('property', 'og:locale', currentLocaleConfig?.ogLocale);
    if (page.ogImage) {
      upsertMeta('property', 'og:image', new URL(page.ogImage, origin).href);
      upsertMeta('property', 'og:image:alt', ogImageAlt);
    }
    replaceMetaGroup('og:locale:alternate', LOCALES.filter((l) => l.code !== locale).map((l) => l.ogLocale));

    upsertMeta('name', 'twitter:card', page.ogImage ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', metaTitle || title);
    upsertMeta('name', 'twitter:description', metaDescription);
    if (page.ogImage) {
      upsertMeta('name', 'twitter:image', new URL(page.ogImage, origin).href);
      upsertMeta('name', 'twitter:image:alt', ogImageAlt);
    }

    // hreflang: dice a Google quali URL sono la stessa pagina in altre lingue
    // — solo per le lingue in cui la pagina ha davvero un metaTitle tradotto.
    // La rotta esiste comunque per ogni lingua (il router mostra il fallback
    // IT se manca la traduzione), ma senza un hreflang che la dichiari come
    // versione linguistica a sé Google non la tratta come contenuto duplicato
    // sotto lingue diverse (coerente con scripts/generate-sitemap.js).
    LOCALES.forEach((loc) => {
      const translated = loc.code === DEFAULT_LOCALE || (isLocaleMap(page.metaTitle) && !!page.metaTitle[loc.code]?.trim());
      if (translated) upsertLink('alternate', loc.code, origin + localizedPath(page.slug, loc.code));
      else removeLink('alternate', loc.code);
    });
    upsertLink('alternate', 'x-default', origin + localizedPath(page.slug, DEFAULT_LOCALE));
  }, [page, locale]);

  return null;
}
